import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import {
  AnimatedScreen,
  EmptyTable,
  PageHeader,
  Paginate,
  Table,
} from 'Shared/components';
import { booleanValueMapping, hasScopedPermission } from 'Shared/utils';
import {
  MoratoriumsFilters,
  MoratoriumsFilterType,
} from './MoratoriumsFilters';
import { useQuery } from '@apollo/react-hooks';
import { GET_MORATORIUM_EVENTS } from 'Admin/Partners/graphql';
import { DateTime } from 'luxon';
import { useAuth0 } from 'Auth';
import { CreateMoratoriumModal } from './CreateMoratoriumModal';

type MoratoriumEvent = {
  externalId: string;
  name: string;
  eventType: string;
  type: string;
  description: string;
  startDate: string;
  endDate: string;
  active: boolean;
};

type MoratoriumEventResponse = {
  moratoriumEvents: MoratoriumEvent[];
};

type PageInfo = {
  limit: number;
  offset: number;
  total: number;
};

export const Moratoriums: React.FC<RouteComponentProps> = ({ history }) => {
  const { user } = useAuth0();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const hasViewPermission = hasScopedPermission(
    user?.effectiveGrants || [],
    'partner',
    'moratoriums.read'
  );
  const hasUpdatePermission = hasScopedPermission(
    user?.effectiveGrants || [],
    'partner',
    'moratoriums.update'
  );

  useEffect(() => {
    if (!hasViewPermission) {
      history.replace('/');
    }
  }, [hasViewPermission, history]);

  const [filters, setFilters] = useState<MoratoriumsFilterType>({
    active: 'true',
  });
  const [pagination, setPagination] = useState<PageInfo>({
    limit: 10,
    offset: 0,
    total: 0,
  });

  const { networkStatus, error, data, refetch } = useQuery<
    MoratoriumEventResponse
  >(GET_MORATORIUM_EVENTS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Apply filters using useMemo to ensure it always runs when data or filters change
  const filteredEvents = useMemo(() => {
    if (!data?.moratoriumEvents) {
      return [];
    }

    return data.moratoriumEvents.filter(event => {
      let matches = true;

      // Filter by name
      if (filters.name) {
        matches =
          matches &&
          event.name
            .toLocaleLowerCase()
            .includes(filters.name.toLocaleLowerCase());
      }

      // Filter by active status (default to 'true' if not set)
      const activeFilter = filters.active || 'true';
      matches = matches && event.active === (activeFilter === 'true');

      return matches;
    });
  }, [data?.moratoriumEvents, filters]);

  // Update pagination when filtered events change
  useEffect(() => {
    setPagination(prevState => ({
      ...prevState,
      offset: 0,
      total: filteredEvents.length,
    }));
  }, [filteredEvents]);

  const onChangeFilters = useCallback((newFilters: MoratoriumsFilterType) => {
    setFilters(newFilters);
  }, []);

  return (
    <AnimatedScreen
      testId="all-moratoriums-home"
      loading={networkStatus === 1}
      error={error && error.message}
    >
      <PageHeader
        heading="Moratorium Events"
        actionName={hasUpdatePermission ? 'Create Event' : undefined}
        onActionClick={
          hasUpdatePermission ? () => setShowCreateModal(true) : undefined
        }
      />

      <MoratoriumsFilters filters={filters} onChangeFilters={onChangeFilters} />
      {filteredEvents && filteredEvents.length ? (
        <>
          <Table
            headers={[
              'Name',
              'Description',
              'Start Date',
              'End Date',
              'Active',
              '',
            ]}
            columnWidths={[1, 1, 2, 1, 1, 1, '60px']}
            name="all-moratoriums-table"
            rows={filteredEvents
              .slice(pagination.offset, pagination.offset + pagination.limit)
              .map(moratoriumEvents => ({
                key: moratoriumEvents.externalId,
                data: [
                  moratoriumEvents.name,
                  moratoriumEvents.description,
                  DateTime.fromISO(moratoriumEvents.startDate, {
                    zone: 'utc',
                  }).toFormat('MM/dd/yyyy hh:mm a'),
                  DateTime.fromISO(moratoriumEvents.endDate, {
                    zone: 'utc',
                  }).toFormat('MM/dd/yyyy hh:mm a'),
                  booleanValueMapping[moratoriumEvents.active.toString()],
                  <Link
                    to={`/tools/moratoriums/${moratoriumEvents.externalId}`}
                  >
                    View
                  </Link>,
                ],
              }))}
            loading={[2, 3, 4].includes(networkStatus)}
            hasPagination
          />
          <Paginate
            total={pagination?.total || 0}
            offset={pagination?.offset}
            limit={pagination?.limit}
            onOffsetChange={(limit, offset) => {
              setPagination(prevState => ({ ...prevState, limit, offset }));
            }}
            onLimitChange={limit => {
              setPagination(prevState => ({ ...prevState, limit, offset: 0 }));
            }}
          />
        </>
      ) : (
        <EmptyTable>No results found</EmptyTable>
      )}

      {hasUpdatePermission && (
        <CreateMoratoriumModal
          showModal={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}
    </AnimatedScreen>
  );
};
